import React, { useCallback, useEffect, useRef, useState } from "react";
import RoomData from "../../../../interfaces/robot/rooms/room-data";
import { useSharedActions, useSharedDispatch, useSharedSelector } from "../../../../store/SharedProvider";
import EditMapUndoRedoAction from "../../../../interfaces/robot/zone-planner/edit-map-undo-redo-action";
import ZoneControllerMouseState from "../../../../enums/robot/zone-planner/zone-controller-mouse-state";
import { convertUint8ArrayToString, getCanvasPointFromMouseClick } from "../../../../utils/canvas-helper";
import { selectZonePlannerMouseState } from "../../../../store/room/zone-planner/zone-planner-selectors";
import { selectMapManualEditStatus, selectZonePlannerMapDrawColor } from "../../../../store/room/maps/maps-selectors";
import CustomSnackbar from "../../../Feedback/CustomSnackbar";
import { useTranslation } from "react-i18next";
import HttpFetchStatus from "../../../../enums/api/http-fetch-status";

const EditMapPixels: React.FC<{
  roomData: RoomData;
  zoomScale: number;
  mapImgUrl: string;
  mapData: Uint8ClampedArray | null;
  onEditMap: (mapData: Uint8ClampedArray, action: EditMapUndoRedoAction[] | null) => void;
  inMappingWizard: boolean | undefined;
}> = ({ roomData, zoomScale, mapImgUrl, onEditMap, mapData, inMappingWizard }) => {
  const { t } = useTranslation();

  const dispatch = useSharedDispatch();
  const isFirstLoad = useRef(true);

  const drawMapColor = useSharedSelector((state) => selectZonePlannerMapDrawColor(state));
  const zonePlannerMouseState = useSharedSelector((state) => selectZonePlannerMouseState(state));
  const editMapStatus = useSharedSelector((state) => selectMapManualEditStatus(state));

  const { setEditMapUpdateAction, putEditedMapByRoomId, resetMapEditStatusState, archiveMapByRoomId } =
    useSharedActions();

  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  const [rectangleCoords, setRectangleCoords] = useState<{
    initialX: number;
    initialY: number;
    endX: number;
    endY: number;
  } | null>(null);
  const [drawingPoints, setDrawingPoints] = useState<EditMapUndoRedoAction[]>([]);

  useEffect(() => {
    if (editMapStatus?.toDoAction != null) {
      const ctx = getCtxFromCanvas(canvasRef);
      if (ctx == null) {
        return;
      }
      editMapStatus.toDoAction.forEach((point) => {
        const imageData = ctx.createImageData(1, 1);
        const data = imageData.data;
        data[0] = point.oldColor;
        data[1] = point.oldColor;
        data[2] = point.oldColor;
        data[3] = 255;
        ctx.putImageData(imageData, point.x, point.y);
      });
      dispatch(setEditMapUpdateAction(null));
      const imageData = ctx.getImageData(0, 0, roomData.mapInformation.width, roomData.mapInformation.height).data;
      onEditMap(imageData, null);
    }
  }, [
    dispatch,
    drawingPoints,
    editMapStatus?.toDoAction,
    onEditMap,
    roomData.mapInformation.height,
    roomData.mapInformation.width,
  ]);

  useEffect(() => {
    dispatch(archiveMapByRoomId(roomData.id));
  }, []);

  useEffect(() => {
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }
    if (mapData != null) {
      const base64String = convertUint8ArrayToString(mapData);
      dispatch(
        putEditedMapByRoomId({
          roomId: roomData.id,
          base64String: base64String,
        }),
      );
    }
  }, [mapData]);

  const canvasRef = useRef<HTMLCanvasElement>(null);

  const getCtxFromCanvas = (canvasRef: React.RefObject<HTMLCanvasElement>) => {
    if (canvasRef.current == null) {
      return null;
    }

    const ctx = canvasRef.current.getContext("2d", { willReadFrequently: true });
    if (ctx == null) {
      return null;
    }

    ctx.imageSmoothingEnabled = false;
    return ctx;
  };

  const handleMouseTouchDown = (event: React.MouseEvent | React.TouchEvent) => {
    if (zonePlannerMouseState === ZoneControllerMouseState.DrawPenTool) {
      setIsDrawing(true);
      handleMouseTouchMove(event);
    } else if (
      (zonePlannerMouseState === ZoneControllerMouseState.DrawRectangleTool ||
        zonePlannerMouseState === ZoneControllerMouseState.DrawLineTool) &&
      canvasRef.current != null
    ) {
      const ctx = getCtxFromCanvas(canvasRef);
      if (ctx == null) {
        return;
      }

      const canvasPoint = getCanvasPointFromMouseClick(event, canvasRef, zoomScale);
      if (canvasPoint == null) {
        return;
      }

      setRectangleCoords({
        initialX: Math.floor(canvasPoint.x),
        initialY: Math.floor(canvasPoint.y),
        endX: Math.floor(canvasPoint.x),
        endY: Math.floor(canvasPoint.y),
      });
    }
  };

  const handleMouseTouchUp = (event: React.MouseEvent | React.TouchEvent) => {
    const ctx = getCtxFromCanvas(canvasRef);
    if (ctx == null || drawMapColor == null) {
      return;
    }

    if (isDrawing && zonePlannerMouseState === ZoneControllerMouseState.DrawPenTool) {
      const imageData = ctx.getImageData(0, 0, roomData.mapInformation.width, roomData.mapInformation.height).data;
      onEditMap(imageData, drawingPoints);
      setDrawingPoints([]);
    } else if (
      zonePlannerMouseState === ZoneControllerMouseState.DrawLineTool &&
      rectangleCoords != null &&
      canvasRef.current != null
    ) {
      ctx.fillStyle = `rgb(${drawMapColor} ${drawMapColor} ${drawMapColor})`;
      const undoAction: EditMapUndoRedoAction[] = [];

      const angle = Math.atan2(
        rectangleCoords.endY - rectangleCoords.initialY,
        rectangleCoords.endX - rectangleCoords.initialX,
      );
      const length = Math.sqrt(
        (rectangleCoords.endY - rectangleCoords.initialY) ** 2 + (rectangleCoords.endX - rectangleCoords.initialX) ** 2,
      );

      for (let step = 0; step < length; step++) {
        const x = Math.floor(step * Math.cos(angle) + rectangleCoords.initialX);
        const y = Math.floor(step * Math.sin(angle) + rectangleCoords.initialY);
        if (undoAction.find((action) => action.x === x && action.y === y) == null) {
          undoAction.push({ x, y, newColor: drawMapColor, oldColor: ctx.getImageData(x, y, 1, 1).data[0] });
          const imageData = ctx.createImageData(1, 1);
          const data = imageData.data;
          data[0] = drawMapColor;
          data[1] = drawMapColor;
          data[2] = drawMapColor;
          data[3] = 255;
          ctx.putImageData(imageData, x, y);
        }
      }
      const imageData = ctx.getImageData(0, 0, roomData.mapInformation.width, roomData.mapInformation.height).data;
      onEditMap(imageData, undoAction);
    } else if (
      zonePlannerMouseState === ZoneControllerMouseState.DrawRectangleTool &&
      rectangleCoords != null &&
      canvasRef.current != null
    ) {
      ctx.fillStyle = `rgb(${drawMapColor} ${drawMapColor} ${drawMapColor})`;
      const undoAction: EditMapUndoRedoAction[] = [];
      for (
        let xMove = 0;
        xMove <
        (Math.abs(rectangleCoords.initialX - rectangleCoords.endX) < 1
          ? 1
          : Math.abs(rectangleCoords.endX - rectangleCoords.initialX));
        xMove++
      ) {
        for (
          let yMove = 0;
          yMove <
          (Math.abs(rectangleCoords.initialY - rectangleCoords.endY) < 1
            ? 1
            : Math.abs(rectangleCoords.endY - rectangleCoords.initialY));
          yMove++
        ) {
          const x =
            rectangleCoords.initialX -
            (Math.sign(rectangleCoords.endX - rectangleCoords.initialX) === -1 ? 1 : 0) +
            xMove * Math.sign(rectangleCoords.endX - rectangleCoords.initialX);
          const y =
            rectangleCoords.initialY -
            (Math.sign(rectangleCoords.endY - rectangleCoords.initialY) === -1 ? 1 : 0) +
            yMove * Math.sign(rectangleCoords.endY - rectangleCoords.initialY);
          undoAction.push({ x, y, newColor: drawMapColor, oldColor: ctx.getImageData(x, y, 1, 1).data[0] });
        }
      }
      ctx.fillRect(
        rectangleCoords.initialX,
        rectangleCoords.initialY,
        Math.abs(rectangleCoords.initialX - rectangleCoords.endX) < 1
          ? 1
          : rectangleCoords.endX - rectangleCoords.initialX,
        Math.abs(rectangleCoords.initialY - rectangleCoords.endY) < 1
          ? 1
          : rectangleCoords.endY - rectangleCoords.initialY,
      );

      const imageData = ctx.getImageData(0, 0, roomData.mapInformation.width, roomData.mapInformation.height).data;
      onEditMap(imageData, undoAction);
    }
    setIsDrawing(false);
    setRectangleCoords(null);
  };

  const handleMouseTouchMove = (event: React.MouseEvent | React.TouchEvent) => {
    if (canvasRef.current != null && drawMapColor != null) {
      const ctx = getCtxFromCanvas(canvasRef);
      if (ctx == null) {
        return;
      }

      const canvasPoint = getCanvasPointFromMouseClick(event, canvasRef, zoomScale);
      if (canvasPoint == null) {
        return;
      }

      if (
        (zonePlannerMouseState === ZoneControllerMouseState.DrawRectangleTool ||
          zonePlannerMouseState === ZoneControllerMouseState.DrawLineTool) &&
        rectangleCoords != null
      ) {
        setRectangleCoords((prevState) => ({
          ...prevState!,
          endX: Math.floor(canvasPoint.x),
          endY: Math.floor(canvasPoint.y),
        }));
      } else if (zonePlannerMouseState === ZoneControllerMouseState.DrawPenTool && isDrawing) {
        const x = Math.floor(canvasPoint.x);
        const y = Math.floor(canvasPoint.y);
        const oldColor = ctx.getImageData(x, y, 1, 1).data[0];
        const imageData = ctx.createImageData(1, 1);
        const data = imageData.data;
        data[0] = drawMapColor;
        data[1] = drawMapColor;
        data[2] = drawMapColor;
        data[3] = 255;

        setDrawingPoints((prevState) => {
          if (prevState.length === 0 || !prevState.some((point) => point.x === x && point.y === y)) {
            return [...prevState, { x, y, newColor: drawMapColor, oldColor }];
          }
          return prevState;
        });
        ctx.putImageData(imageData, x, y);
      }
    }
  };

  const initCanvas = useCallback(() => {
    if (!canvasRef.current) return;

    const ctx = getCtxFromCanvas(canvasRef);
    if (ctx == null) {
      return;
    }

    const image = new Image();
    image.crossOrigin = "Anonymous";
    image.src = `data:image/png;base64,${mapImgUrl}`;
    image.onload = () => {
      ctx.drawImage(image, 0, 0, roomData.mapInformation.width, roomData.mapInformation.height);
    };
  }, [mapImgUrl, roomData.mapInformation.height, roomData.mapInformation.width]);

  useEffect(() => {
    const ctx = getCtxFromCanvas(canvasRef);
    if (ctx == null) return;

    if (mapData == null) {
      initCanvas();
    } else {
      const imageData = ctx.createImageData(roomData.mapInformation.width, roomData.mapInformation.height);
      imageData.data.set(mapData);
      ctx.putImageData(imageData, 0, 0);
    }
  }, [initCanvas, mapData]);

  const resetMapEditStatusStateHandler = () => {
    dispatch(resetMapEditStatusState());
  };

  return (
    <React.Fragment>
      <div
        style={{
          cursor:
            zonePlannerMouseState === ZoneControllerMouseState.DrawPenTool
              ? `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 30 30">` +
                `<circle cx="5" cy="5" r="5" strokeWidth="1" fill="rgb(${drawMapColor} ${drawMapColor} ${drawMapColor})" stroke="white"/>` +
                `<path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" stroke="white" stroke-width="1"></path>` +
                `</svg>') 0 10, auto`
              : (zonePlannerMouseState === ZoneControllerMouseState.DrawRectangleTool ||
                    zonePlannerMouseState === ZoneControllerMouseState.DrawLineTool) &&
                  drawMapColor != null
                ? `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="20" width="20" viewBox="0 0 30 30">` +
                  `<circle cx="15" cy="15" r="5" strokeWidth="1" fill="rgb(${drawMapColor} ${drawMapColor} ${drawMapColor})"/>` +
                  `<circle cx="15" cy="15" r="15" strokeWidth="1" opacity="0.5" fill="rgb(${drawMapColor - 15} ${
                    drawMapColor - 15
                  } ${drawMapColor - 15})"/>` +
                  `</svg>') 10 10, auto`
                : undefined,
        }}
      >
        <canvas
          ref={canvasRef}
          width={roomData.mapInformation.width}
          height={roomData.mapInformation.height}
          onMouseDown={handleMouseTouchDown}
          onTouchStart={handleMouseTouchDown}
          onMouseUp={handleMouseTouchUp}
          onTouchEnd={handleMouseTouchUp}
          onMouseMove={handleMouseTouchMove}
          onTouchMove={handleMouseTouchMove}
        />
        <svg
          viewBox={`0 0 ${roomData.mapInformation.width} ${roomData.mapInformation.height}`}
          style={{
            pointerEvents: "none",
            position: "absolute",
            left: 0,
            right: 0,
            height: roomData.mapInformation.height,
            width: roomData.mapInformation.width,
          }}
        >
          {rectangleCoords != null &&
            drawMapColor != null &&
            zonePlannerMouseState === ZoneControllerMouseState.DrawRectangleTool && (
              <polygon
                points={`${rectangleCoords.initialX},${rectangleCoords.initialY} ${rectangleCoords.endX},${rectangleCoords.initialY} ${rectangleCoords.endX},${rectangleCoords.endY} ${rectangleCoords.initialX},${rectangleCoords.endY}`}
                style={{
                  fill: `rgb(${drawMapColor} ${drawMapColor} ${drawMapColor})`,
                  stroke: `rgb(${drawMapColor - 15} ${drawMapColor - 15} ${drawMapColor - 15})`,
                }}
              />
            )}
          {rectangleCoords != null &&
            drawMapColor != null &&
            zonePlannerMouseState === ZoneControllerMouseState.DrawLineTool && (
              <polygon
                points={`${rectangleCoords.initialX},${rectangleCoords.initialY} ${rectangleCoords.endX},${rectangleCoords.endY}`}
                style={{
                  fill: `rgb(${drawMapColor} ${drawMapColor} ${drawMapColor})`,
                  stroke: `rgb(${drawMapColor - 15} ${drawMapColor - 15} ${drawMapColor - 15})`,
                }}
              />
            )}
        </svg>
      </div>
      {!inMappingWizard && 
        <CustomSnackbar
          title={t("success-saving-room")}
          open={editMapStatus?.status === HttpFetchStatus.Success}
          severity="success"
          onClose={resetMapEditStatusStateHandler}
        />
      }
      {!inMappingWizard && 
        <CustomSnackbar
        title={t("error-saving-room")}
        open={editMapStatus?.status === HttpFetchStatus.Failed}
        severity="error"
        onClose={resetMapEditStatusStateHandler} 
        />
      }
    </React.Fragment>
  );
};

export default EditMapPixels;
